import { FC, useMemo } from 'react';

import dayjs from 'dayjs';
import useSWR from 'swr';

import Chip from '@mui/material/Chip';

import { TournamentsHistoryActionColumn } from './tournamentsHistoryTableColumns/TournamentsHistoryActionsColumn';

import css from './TournamentsHistoryPage.module.css';

import { tournamentsService } from 'v2/api/tournamentsService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { ImageButton } from 'v2/components/imageButton/ImageButton';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { TournamentModel } from 'v2/types/TournamentModel';
import { fileSrc } from 'v2/utils';

export const TournamentsHistoryPage: FC = () => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 30 });

  const { data, mutate } = useSWR(
    ['getTournamentsHistory', page, pageSize, { isArchived: true }],
    tournamentsService.getTournamentsHistory,
    {
      keepPreviousData: true,
    }
  );

  const columns: TableColumn<TournamentModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Tournament',
        ellipsis: true,
        render: (row) => (
          <div className={css.tournamentsHistoryTableCell}>
            <ImageButton src={fileSrc(row.images.image390Id)} alt={row.name} />

            <p>{row.name}</p>
          </div>
        ),
      },
      {
        key: 'providerNames',
        label: 'Providers',
        render: (row) => (
          <div className={css.tournamentsHistoryTableCell}>
            {row.providerNames.map((p) => (
              <Chip key={p} label={p} size="small" />
            ))}
          </div>
        ),
      },
      {
        key: 'startDate',
        label: 'Start Date',
        render: (row) => dayjs(row.startDate).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'endDate',
        label: 'End Date',
        render: (row) => dayjs(row.endDate).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <TournamentsHistoryActionColumn tournament={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  return (
    <PageLayout className={css.tournamentsHistoryPage} overflow="hidden">
      <PageTitle>Tournaments History</PageTitle>

      <DataTable
        columns={columns}
        data={data?.result}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        withPagination
      />
    </PageLayout>
  );
};
